// src/pages/Jobs/JobSearchPage.tsx
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, CardBody, Form, FormGroup, Label, Input, Button, Alert } from 'reactstrap';
import { Search, MapPin, Filter } from 'lucide-react';
import Header from '../Home/Header';
import Footer from '../Home/Footer';
import JobListingLayout from './JobListingLayout';
import SEOTags from '../Listing/SEOTags';
import AutoBreadCrumb from '../../Components/Common/AutoBreadCrumb';
import { Job } from '../../models/Job.interface';
import { jobAPI, LocationSearchParams } from '../../services/jobService';

interface SearchFilters {
  query: string;
  location: string;
  locationType: 'city' | 'zipcode' | 'address';
  radius: number;
  categories: string[];
  status: string[];
}

const JobSearchPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [center, setCenter] = useState<{ latitude: number; longitude: number } | undefined>();
  const [userLocation, setUserLocation] = useState<{ latitude: number; longitude: number } | undefined>();

  const [filters, setFilters] = useState<SearchFilters>({
    query: searchParams.get('q') || '',
    location: searchParams.get('location') || '',
    locationType: (searchParams.get('type') as 'city' | 'zipcode' | 'address') || 'city',
    radius: parseInt(searchParams.get('radius') || '10'),
    categories: searchParams.get('categories')?.split(',') || [],
    status: ['Published', 'Hiring', 'Pending Inspection'],
  });

  const [availableCategories, setAvailableCategories] = useState<string[]>([]);

  // Load initial data
  useEffect(() => {
    loadCategories();
    getUserLocation();
    
    // Perform search if we have parameters
    if (filters.location || filters.query) {
      handleSearch();
    }
  }, []);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.query) params.set('q', filters.query);
    if (filters.location) params.set('location', filters.location);
    if (filters.locationType !== 'city') params.set('type', filters.locationType);
    if (filters.radius !== 10) params.set('radius', filters.radius.toString());
    if (filters.categories.length > 0) params.set('categories', filters.categories.join(','));
    
    setSearchParams(params);
  }, [filters, setSearchParams]);

  const loadCategories = async () => {
    try {
      const categories = await jobAPI.getJobCategories();
      setAvailableCategories(categories.map(c => c.category));
    } catch (err) {
      console.error('Error loading categories:', err);
    }
  };

  const getUserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          };
          setUserLocation(location);
          if (!center) {
            setCenter(location);
          }
        },
        (error) => {
          console.log('Geolocation error:', error);
        }
      );
    }
  };

  const handleSearch = async () => {
    if (!filters.location && !filters.query) {
      setError('Please enter a location or search term');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let response;
      
      if (filters.location) {
        // First geocode the location
        const locationParams: LocationSearchParams = {
          query: filters.location,
          type: filters.locationType,
          radius: filters.radius
        };

        const geocoded = await jobAPI.geocodeLocation(locationParams);
        
        // Set map center to geocoded location
        setCenter({
          latitude: geocoded.latitude,
          longitude: geocoded.longitude
        });

        // Search jobs by location
        if (filters.locationType === 'zipcode') {
          response = await jobAPI.getJobsByZipCode(
            filters.location,
            filters.radius,
            {
              categories: filters.categories,
              status: filters.status,
              limit: 50
            }
          );
        } else {
          response = await jobAPI.getJobsByLocation(
            geocoded.latitude,
            geocoded.longitude,
            filters.radius,
            {
              categories: filters.categories,
              status: filters.status,
              limit: 50
            }
          );
        }
      } else {
        // Text search without location
        response = await jobAPI.searchJobs(
          filters.query,
          undefined,
          {
            categories: filters.categories,
            status: filters.status,
            limit: 50
          }
        );
      }

      setJobs(response.jobs || []);
      setTotalCount(response.total || 0);

    } catch (err) {
      console.error('Error searching jobs:', err);
      setError('Failed to search jobs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: keyof SearchFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleCategoryToggle = (category: string) => {
    setFilters(prev => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category]
    }));
  };

  const handleJobClick = (job: Job) => {
    navigate(`/job-detail/${job.id}`);
  };

  const handleBoundsChange = async (bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  }) => {
    // Update search results based on map bounds
    try {
      const response = await jobAPI.getJobsByBounds(bounds, {
        categories: filters.categories,
        status: filters.status,
        limit: 100
      });
      
      setJobs(response.jobs || []);
      setTotalCount(response.total || 0);
    } catch (err) {
      console.error('Error updating jobs by bounds:', err);
    }
  };

  const detectLocationType = (input: string): 'city' | 'zipcode' | 'address' => {
    // Simple detection logic
    if (/^\d{5}(-\d{4})?$/.test(input)) {
      return 'zipcode';
    } else if (input.includes(',') && input.split(',').length >= 2) {
      return 'city';
    } else if (input.includes(' ') && /\d/.test(input)) {
      return 'address';
    }
    return 'city';
  };

  const handleLocationInputChange = (value: string) => {
    const detectedType = detectLocationType(value);
    setFilters(prev => ({
      ...prev,
      location: value,
      locationType: detectedType
    }));
  };

  const renderSearchForm = () => (
    <Card className="mb-4">
      <CardBody>
        <Form onSubmit={(e) => { e.preventDefault(); handleSearch(); }}>
          <Row>
            <Col md={4}>
              <FormGroup>
                <Label for="query">Search Jobs</Label>
                <Input
                  id="query"
                  type="text"
                  placeholder="Job title, company, keywords..."
                  value={filters.query}
                  onChange={(e) => handleFilterChange('query', e.target.value)}
                />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label for="location">Location</Label>
                <Input
                  id="location"
                  type="text"
                  placeholder="City, State or ZIP code"
                  value={filters.location}
                  onChange={(e) => handleLocationInputChange(e.target.value)}
                />
                <small className="text-muted">
                  Detected: {filters.locationType}
                  {filters.locationType !== 'city' && ` (${filters.radius} miles)`}
                </small>
              </FormGroup>
            </Col>
            <Col md={2}>
              {filters.locationType !== 'city' && (
                <FormGroup>
                  <Label for="radius">Radius (miles)</Label>
                  <Input
                    id="radius"
                    type="select"
                    value={filters.radius}
                    onChange={(e) => handleFilterChange('radius', parseInt(e.target.value))}
                  >
                    <option value={5}>5 miles</option>
                    <option value={10}>10 miles</option>
                    <option value={25}>25 miles</option>
                    <option value={50}>50 miles</option>
                    <option value={100}>100 miles</option>
                  </Input>
                </FormGroup>
              )}
            </Col>
            <Col md={2}>
              <FormGroup>
                <Label>&nbsp;</Label>
                <div className="d-flex gap-2">
                  <Button 
                    color="primary" 
                    type="submit"
                    disabled={loading}
                    className="flex-grow-1"
                  >
                    <Search size={16} />
                  </Button>
                  <Button
                    color="outline-secondary"
                    type="button"
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <Filter size={16} />
                  </Button>
                </div>
              </FormGroup>
            </Col>
          </Row>

          {showFilters && (
            <Row className="mt-3 pt-3 border-top">
              <Col md={6}>
                <FormGroup>
                  <Label>Job Categories</Label>
                  <div className="d-flex flex-wrap gap-2">
                    {availableCategories.slice(0, 10).map(category => (
                      <Button
                        key={category}
                        color={filters.categories.includes(category) ? 'primary' : 'outline-primary'}
                        size="sm"
                        onClick={() => handleCategoryToggle(category)}
                      >
                        {category}
                      </Button>
                    ))}
                  </div>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Job Status</Label>
                  <div className="d-flex flex-wrap gap-2">
                    {['Published', 'Hiring', 'Pending Inspection', 'Under Contract'].map(status => (
                      <Button
                        key={status}
                        color={filters.status.includes(status) ? 'success' : 'outline-success'}
                        size="sm"
                        onClick={() => {
                          const newStatus = filters.status.includes(status)
                            ? filters.status.filter(s => s !== status)
                            : [...filters.status, status];
                          handleFilterChange('status', newStatus);
                        }}
                      >
                        {status}
                      </Button>
                    ))}
                  </div>
                </FormGroup>
              </Col>
            </Row>
          )}
        </Form>
      </CardBody>
    </Card>
  );

  const pageTitle = filters.location || filters.query 
    ? `Jobs ${filters.location ? `in ${filters.location}` : ''} ${filters.query ? `- ${filters.query}` : ''}`
    : 'Search Jobs';
  
  const pageDescription = `Find job opportunities ${filters.location ? `in ${filters.location}` : 'nationwide'}. ${totalCount > 0 ? `${totalCount} jobs found` : 'Search thousands of job listings'}.`;

  return (
    <React.Fragment>
      <SEOTags
        title={pageTitle}
        description={pageDescription}
        keywords={[
          'jobs',
          'employment',
          'career opportunities',
          'job search',
          ...(filters.location ? [filters.location] : []),
          ...(filters.query ? [filters.query] : []),
          ...filters.categories
        ]}
        schemaType="WebPage"
      />

      <Header />
      
      <div className="page-content">
        <Container fluid className="px-3">
          <AutoBreadCrumb />
          
          {renderSearchForm()}

          {error && (
            <Alert color="danger" className="mb-4">
              {error}
            </Alert>
          )}

          <JobListingLayout
            jobs={jobs}
            loading={loading}
            center={center}
            onJobClick={handleJobClick}
            onBoundsChange={handleBoundsChange}
            title="Search Results"
            subtitle={loading 
              ? 'Searching...' 
              : totalCount > 0 
                ? `${totalCount} job${totalCount !== 1 ? 's' : ''} found`
                : jobs.length === 0 && !loading && (filters.location || filters.query)
                  ? 'No jobs found matching your criteria'
                  : 'Enter search criteria above'
            }
            showDistance={!!userLocation}
            userLocation={userLocation}
          />
        </Container>
      </div>

      <Footer />
    </React.Fragment>
  );
};

export default JobSearchPage;